import React from "react";
import { Stack, Box, Typography, Avatar, Chip } from "@mui/material";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyProgress } from "@/hooks/useMyProgress";
import { avatars } from "@/utils/profileMetaData";
import { catchphrases } from "@/utils/profileMetaData";
import CatchphraseBanner from "@/assets/background/catchphrase-banner.svg";
import AltAvatar from "@/components/alt-avatar";

// Role-specific configurations
const roleStyles = {
  teacher: {
    backgroundColor: "rgba(142, 51, 255, 0.16)",
    textColor: "#5119B7",
    label: "Coach",
  },
  climber: {
    backgroundColor: "rgba(7, 141, 238, 0.16)",
    textColor: "#0351AB",
    label: "Climber",
  },
  schoolstaff: {
    backgroundColor: "rgba(255, 171, 0, 0.16)",
    textColor: "#B76E00",
    label: "Staff",
  },
  districtadmin: {
    backgroundColor: "rgba(0, 184, 217, 0.16)",
    textColor: "#006C9C",
    label: "Administrator",
  },
};

const NavigationIdentifier = ({ sx }) => {
  const currentUser = useCurrentUser();
  const { profileChoices } = useMyProgress();

  const userAvatar = avatars.find(
    (avatar) => avatar.id === profileChoices?.avatar,
  )
    ? avatars.find((avatar) => avatar.id === profileChoices?.avatar).src
    : null;
  const userRole = roleStyles[currentUser.role];
  const catchphrase = catchphrases.find(
    (phrase) => phrase.id === profileChoices?.catchphrase,
  );

  return (
    <Box
      sx={{
        // Fluid, not a fixed 272px: the nav is 280px wide and the scroll
        // gutter eats ~16px of it, so a fixed width overflowed and put a
        // horizontal scrollbar under the whole nav.
        width: "100%",
        textAlign: "center",
        borderRadius: 2,
        backgroundColor: "inherit",
        ...sx,
      }}
    >
      {userAvatar ? (
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontWeight: "bold",
            margin: "0 auto",
          }}
          src={userAvatar}
        ></Avatar>
      ) : (
        <Stack
          sx={{
            margin: "0 auto",
          }}
        >
          <AltAvatar
            initials={`${currentUser.firstName?.[0]}${currentUser.lastName?.[0]}`.toUpperCase()}
            sx={{ margin: "0 auto" }}
          />
        </Stack>
      )}

      {catchphrase && catchphrase.id !== 6 && (
        <Box
          sx={{
            backgroundImage: `url(${CatchphraseBanner})`,
            height: "44px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            mt: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontStyle: "italic",
              fontWeight: "700",
              fontFamily: "ProximaNova Bold",
              pt: "3px",
            }}
          >
            {catchphrase.text}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        {currentUser.firstName} {currentUser.lastName}
      </Typography>

      <Chip
        label={userRole?.label}
        sx={{
          mt: 0.5,
          backgroundColor: userRole?.backgroundColor,
          color: userRole?.textColor,
          fontFamily: "ProximaNova Bold",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: userRole?.backgroundColor,
          },
        }}
      />

      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          color: "#637381",
        }}
      >
        {currentUser.username}
      </Typography>
    </Box>
  );
};

export default NavigationIdentifier;
